import { Injectable } from '@angular/core';
import { Restangular } from 'ngx-restangular';

export type InboundFeedStatus = 'active' | 'paused' | 'archived';
export type InboundFeedScopeType = 'team' | 'company' | 'client' | 'vendorProduct' | 'marketingGroup' | 'partner' | 'vendor';
export type InboundFeedTransport = 'email' | 'sftp';
export type InboundImportStatus = 'received' | 'processing' | 'processed' | 'error' | 'rejected' | 'archived';
export type InboundErrorStatus = 'open' | 'resolved' | 'ignored';
export type InboundErrorSeverity = 'error' | 'warning' | 'info';
export type InboundUnmatchedMessageStatus = 'open' | 'resolved' | 'ignored';

export interface InboundFileFeedHandlerFormatField {
  name: string;
  required?: boolean;
  requiredValue?: boolean;
  type?: string;
  formats?: string[];
  acceptedValues?: string[];
  description?: string;
  mapsTo?: string;
}

export interface InboundFileFeedHandlerFormat {
  label: string;
  requiredScopeType?: InboundFeedScopeType | null;
  fileTypes?: string[];
  headerRow?: number | null;
  rejectUnknownColumns?: boolean;
  behavior?: string;
  fields?: InboundFileFeedHandlerFormatField[];
}

export type InboundFileFeedHandlerFormats = Record<string, InboundFileFeedHandlerFormat>;

export interface InboundFileFeedCapabilities {
  sftpProcessing?: { configured: boolean; modes: string[]; retrySafeHandlers: string[] };
  statuses: InboundFeedStatus[];
  scopeTypes: InboundFeedScopeType[];
  handlerTypes: string[];
  transports: InboundFeedTransport[];
  importStatuses: InboundImportStatus[];
  errorStatuses: InboundErrorStatus[];
  errorSeverities: InboundErrorSeverity[];
  handlerTemplates?: Record<string, string[]>;
  handlerFormats?: InboundFileFeedHandlerFormats;
  inboundAddresses: {
    email?: string;
    sftpHost?: string;
    sftpPort?: number;
  };
  defaults: {
    status?: InboundFeedStatus;
    transportConfig?: Record<string, any>;
    parserConfig?: Record<string, any>;
    validationConfig?: Record<string, any>;
    notificationConfig?: Record<string, any>;
  };
}

export interface InboundFileFeedPollRunnerStatus {
  environment?: string | null;
  isJobRunner?: boolean;
}

export interface InboundFileFeedTransportPollStatus {
  transport: InboundFeedTransport | string;
  cadenceLabel?: string | null;
  cronTime?: string | null;
  timeZone?: string | null;
  mailbox?: string | null;
  nextPollAt?: string | null;
  lastRunStatus?: string | null;
  lastStartedAt?: string | null;
  lastFinishedAt?: string | null;
  lastSucceededAt?: string | null;
  lastFailedAt?: string | null;
  lastDurationMs?: number | null;
  lastErrorMessage?: string | null;
  updatedAt?: string | null;
  runner?: InboundFileFeedPollRunnerStatus | null;
}

export interface InboundFileFeedPollStatus {
  email?: InboundFileFeedTransportPollStatus | null;
}

export interface InboundFileFeedScopeOption {
  id: string;
  label: string;
  vendorSlug?: string;
  productSlug?: string;
}

export type InboundFileFeedScopeOptions = Record<InboundFeedScopeType, InboundFileFeedScopeOption[]> & {
  _meta?: {
    requiresScopeTypeOrFilter?: boolean;
  };
};

export interface InboundFileFeed {
  id: string;
  feedKey: string;
  name: string;
  description?: string | null;
  status: InboundFeedStatus;
  scopeType: InboundFeedScopeType;
  scopeId: string;
  handlerType: string;
  transportConfig: Record<string, any>;
  parserConfig: Record<string, any>;
  validationConfig: Record<string, any>;
  notificationConfig: Record<string, any>;
  metadata: Record<string, any>;
  lastImportId?: string | null;
  lastImportStatus?: string | null;
  lastImportAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdByUserId?: string | null;
  updatedByUserId?: string | null;
}

export interface InboundFileImport {
  id: string;
  feedId: string;
  status: InboundImportStatus;
  transport: InboundFeedTransport;
  sourceIdentifier?: string | null;
  subject?: string | null;
  originalFileName?: string | null;
  storageBucket?: string | null;
  storageKey?: string | null;
  checksum?: string | null;
  idempotencyKey?: string | null;
  rowCount?: number | null;
  successCount?: number | null;
  errorCount?: number | null;
  warningCount?: number | null;
  receivedAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  errorSummary?: string | null;
  resultSummary?: Record<string, any> | null;
  metadata?: Record<string, any> | null;
}

export interface InboundFileImportError {
  id: string;
  importId: string;
  feedId: string;
  rowNumber?: number | null;
  fieldName?: string | null;
  errorCode?: string | null;
  severity: InboundErrorSeverity;
  message: string;
  rawRow?: Record<string, any> | null;
  status: InboundErrorStatus;
  resolutionNote?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  resolvedAt?: string | null;
  resolvedByUserId?: string | null;
}

export interface InboundFileUnmatchedMessage {
  id?: string;
  messageId?: string;
  createdAt?: string | null;
  transport?: InboundFeedTransport | string | null;
  sourceIdentifier?: string | null;
  subject?: string | null;
  originalFileName?: string | null;
  errorCode?: string | null;
  message: string;
  status: InboundUnmatchedMessageStatus;
  metadata?: Record<string, any> | null;
  checksum?: string | null;
  bodyText?: string | null;
  resolutionNote?: string | null;
  resolvedAt?: string | null;
  resolvedByUserId?: string | null;
}

export interface MarketingGroupEmailMembersImportResult {
  import?: InboundFileImport;
  newCount?: number;
  updatedCount?: number;
  skippedCount?: number;
  errorCount?: number;
  warningCount?: number;
  errorMessage?: string;
  errorDetails?: Array<{
    lineNumber?: number;
    rowNumber?: number;
    fieldName?: string;
    type?: string;
    info?: string;
    message?: string;
  }>;
  warningDetails?: Array<{
    lineNumber?: number;
    rowNumber?: number;
    fieldName?: string;
    type?: string;
    info?: string;
    message?: string;
  }>;
}

export interface InboundFeedListQuery {
  status?: InboundFeedStatus | 'all';
  scopeType?: InboundFeedScopeType | 'all';
  scopeId?: string;
  handlerType?: string;
  transport?: InboundFeedTransport | 'all';
  filter?: string;
  limit?: number;
  offset?: number;
}

export interface InboundImportListQuery {
  feedId?: string;
  status?: InboundImportStatus | 'all';
  limit?: number;
  offset?: number;
}

export interface InboundErrorListQuery {
  feedId?: string;
  importId?: string;
  status?: InboundErrorStatus | 'all';
  severity?: InboundErrorSeverity | 'all';
  limit?: number;
  offset?: number;
}

export interface InboundUnmatchedMessageListQuery {
  status?: InboundUnmatchedMessageStatus | 'all';
  transport?: InboundFeedTransport | 'all';
  filter?: string;
  limit?: number;
  offset?: number;
}

export interface InboundSftpFilePage {
  folder: string;
  files: { name: string; size: number; lastModified: string; etag: string }[];
  nextToken: string | null;
}

export interface InboundSftpUpload {
  processingMode: 'daily' | 'upload';
  firstSeenAt: string;
  canRetry: boolean;
  importAttempts: { id: string; status: string; receivedAt: string }[];
  id: string;
  feedId?: string;
  filePath?: string;
  status: string;
  attempts: number;
  recoveryCount: number;
  importId?: string;
  uploadedAt?: string;
  queuedAt: string | null;
  startedAt?: string;
  finishedAt?: string;
  nextAttemptAt?: string;
  errorCode?: string;
}

export interface InboundSftpAccess {
  configured: boolean;
  status: string;
  message?: string;
  host?: string;
  port?: number;
  uploadPath?: string;
  authMethod?: 'password' | 'ssh_key';
  enabled?: boolean;
  account?: { id: string; username: string; folder: string; status: string; lastError?: string; pendingAction?: string };
  keys?: { id: string; fingerprint: string }[];
  audit?: {
    id: string;
    actorUserId: string;
    actorFirstName: string | null;
    actorLastName: string | null;
    actorEmail: string | null;
    action: string;
    outcome: string;
    createdAt: string;
    errorCode?: string;
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class InboundFileFeedsService {
  constructor(private restangular: Restangular) {}

  capabilities(): Promise<InboundFileFeedCapabilities> {
    return this.base()
      .one('capabilities')
      .get()
      .toPromise()
      .then((capabilities: InboundFileFeedCapabilities) =>
        this.withMarketingGroupEmailMembersCapability(capabilities),
      );
  }

  pollStatus(): Promise<InboundFileFeedPollStatus> {
    return this.base().one('poll-status').get().toPromise();
  }

  runPollNow(): Promise<InboundFileFeedPollStatus> {
    return this.base().one('poll-status').one('run-now').customPOST({}).toPromise();
  }

  scanUploadedFiles(): Promise<{ status: string }> {
    return this.base().one('scan-uploaded-files').customPOST({}).toPromise();
  }

  handlerFormats(): Promise<InboundFileFeedHandlerFormats> {
    return this.base().one('handler-formats').get().toPromise();
  }

  scopeOptions(scopeType: InboundFeedScopeType, filter = '', limit = 250): Promise<InboundFileFeedScopeOptions> {
    return this.base().one('scope-options').get(this.compactQuery({ scopeType, filter, limit })).toPromise();
  }

  listFeeds(query: InboundFeedListQuery = {}): Promise<InboundFileFeed[]> {
    return this.collection().getList(this.compactQuery(query)).toPromise();
  }

  getFeed(id: string): Promise<InboundFileFeed> {
    return this.base().one(id).get().toPromise();
  }

  createFeed(payload: Partial<InboundFileFeed>): Promise<InboundFileFeed> {
    return this.base().customPOST(payload).toPromise();
  }

  updateFeed(id: string, payload: Partial<InboundFileFeed>): Promise<InboundFileFeed> {
    return this.base().one(id).customPATCH(payload).toPromise();
  }

  archiveFeed(id: string): Promise<void> {
    return this.base().one(id).remove().toPromise();
  }

  listSftpUploads(): Promise<InboundSftpUpload[]> {
    return this.base().all('sftp-uploads').getList().toPromise();
  }

  retrySftpUpload(id: string): Promise<{ status: string }> {
    return this.base().one('sftp-uploads', id).all('retry').post({}).toPromise();
  }

  listImports(query: InboundImportListQuery = {}): Promise<InboundFileImport[]> {
    return this.collection().all('imports').getList(this.compactQuery(query)).toPromise();
  }

  listFeedImports(feedId: string, query: InboundImportListQuery = {}): Promise<InboundFileImport[]> {
    return this.base().one(feedId).all('imports').getList(this.compactQuery(query)).toPromise();
  }

  getImport(importId: string): Promise<InboundFileImport> {
    return this.base().one('imports').one(importId).get().toPromise();
  }

  updateImport(importId: string, payload: Partial<InboundFileImport>): Promise<InboundFileImport> {
    return this.base().one('imports').one(importId).customPATCH(payload).toPromise();
  }

  uploadMarketingGroupEmailMembers(groupId: string, data: ArrayBuffer): Promise<MarketingGroupEmailMembersImportResult> {
    return this.base()
      .one('manual-imports')
      .one('marketing_group_email_members')
      .one('marketing-groups', groupId)
      .customPOST(data, undefined, undefined, { 'content-type': 'text/html' })
      .toPromise();
  }

  listErrors(query: InboundErrorListQuery = {}): Promise<InboundFileImportError[]> {
    return this.collection().all('errors').getList(this.compactQuery(query)).toPromise();
  }

  listImportErrors(importId: string, query: InboundErrorListQuery = {}): Promise<InboundFileImportError[]> {
    return this.base().one('imports').one(importId).all('errors').getList(this.compactQuery(query)).toPromise();
  }

  updateError(errorId: string, payload: Partial<InboundFileImportError>): Promise<InboundFileImportError> {
    return this.base().one('errors').one(errorId).customPATCH(payload).toPromise();
  }

  listUnmatchedMessages(query: InboundUnmatchedMessageListQuery = {}): Promise<InboundFileUnmatchedMessage[]> {
    return this.collection().all('unmatched-messages').getList(this.compactQuery(query)).toPromise();
  }

  updateUnmatchedMessage(messageId: string, payload: Partial<InboundFileUnmatchedMessage>): Promise<InboundFileUnmatchedMessage> {
    return this.base().one('unmatched-messages').one(messageId).customPATCH(payload).toPromise();
  }

  listSftpFiles(feedId: string, continuationToken?: string): Promise<InboundSftpFilePage> {
    return this.base().one(feedId).one('sftp-files').get(continuationToken ? { continuationToken } : {}).toPromise();
  }

  sftpAccess(feedId: string): Promise<InboundSftpAccess> {
    return this.base().one(feedId).one('sftp-access').get().toPromise();
  }

  changeSftpAccess(feedId: string, input: Record<string, unknown>): Promise<InboundSftpAccess> {
    return this.base().one(feedId).one('sftp-access').customPOST(input).toPromise();
  }

  sftpFileLink(feedId: string, name: string, mode: 'view' | 'download'): Promise<{ url: string }> {
    return this.base().one(feedId).one('sftp-files').one('content').get({ name, mode }).toPromise();
  }

  changeSftpFile(feedId: string, operation: { action: string; name: string; etag: string; destination?: string }): Promise<unknown> {
    return this.base().one(feedId).one('sftp-files').customPOST(operation).toPromise();
  }

  uploadSftpFile(feedId: string, file: File): Promise<unknown> {
    const data = new FormData();
    data.append('file', file, file.name);
    return this.base().one(feedId).one('sftp-files').one('upload')
      .customPOST(data, undefined, undefined, { 'Content-Type': undefined }).toPromise();
  }

  private base(): any {
    return this.restangular.one('admin').one('inbound-file-feeds');
  }

  private collection(): any {
    return this.restangular.one('admin').all('inbound-file-feeds');
  }

  private withMarketingGroupEmailMembersCapability(
    capabilities: InboundFileFeedCapabilities,
  ): InboundFileFeedCapabilities {
    const marketingGroupEmailMembersHandler = 'marketing_group_email_members';
    const handlerTypes = capabilities.handlerTypes || [];
    const scopeTypes = capabilities.scopeTypes || [];

    return {
      ...capabilities,
      scopeTypes: scopeTypes.includes('marketingGroup')
        ? scopeTypes
        : [...scopeTypes, 'marketingGroup'],
      handlerTypes: handlerTypes.includes(marketingGroupEmailMembersHandler)
        ? handlerTypes
        : [...handlerTypes, marketingGroupEmailMembersHandler],
      handlerFormats: {
        ...(capabilities.handlerFormats || {}),
        [marketingGroupEmailMembersHandler]: capabilities.handlerFormats?.[marketingGroupEmailMembersHandler] || {
          label: 'Marketing group email members',
          requiredScopeType: 'marketingGroup',
          fileTypes: ['csv', 'xlsx'],
          headerRow: 1,
          rejectUnknownColumns: false,
          behavior: 'Adds email-address members to the selected marketing group.',
          fields: [
            {
              name: 'email',
              required: true,
              requiredValue: true,
              type: 'email',
              description: 'Email address to add to the marketing group.',
              mapsTo: 'emailAddress',
            },
          ],
        },
      },
    };
  }

  private compactQuery(query: Record<string, any>): Record<string, any> {
    return Object.keys(query || {}).reduce((result: Record<string, any>, key: string) => {
      const value = query[key];
      if (value === undefined || value === null || value === '' || value === 'all') {
        return result;
      }
      result[key] = value;
      return result;
    }, {});
  }
}
